import {AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Step, Steps, StepService} from "../services/step.service";
import {FormConfig} from "../form/form.config";
import {UntypedFormGroup} from "@angular/forms";
import {Router} from "@angular/router";
import {FormModelRadio} from "../form/form.model.radio";
import {FormModelText} from "../form/form.model.text";
import {FormModelNumber} from "../form/form.model.number";
import {ElementService} from "../services/element.service";
import {Subscription} from "rxjs";
import {PortalService} from "../services/portal.service";

/**
 * Generic step component for all the logic of a step inside a chapter. Can be overwrittert if specific templates
 * are needed or special logic.
 */
@Component({
  selector: 'app-step',
  templateUrl: './step.component.html'
})
export class StepComponent implements OnInit, AfterViewInit, OnDestroy {

  /**
   * Configuration of a form that should be visible here
   */
  formConfig: FormConfig;

  /**
   * the formGroup, created by the formConfig
   */
  form: UntypedFormGroup;

  /**
   * Holds information about the previous and next step.
   */
  steps: Steps;

  /**
   * Holds the previous step object to move back.
   */
  previousStep: Step;

  /**
   * prefix for some textkeys. Can be used to indicate the adviros or others.
   */
  textkeyPrefix: string = "";

  /**
   * toggle if the step is automatically submitted once the form is valid or not
   */
  autoSubmit: boolean = false;

  /**
   * indicator that becomes true, as soon as the view and its child is rendered.
   */
  viewRendered: boolean = false;

  /**
   * deley in ms for the autosubmit
   */
  autoSubmitDelay: number = 200;

  /**
   * list of formfield types that allow an autosubmit
   */
  autoSubmitFieldTypes = [FormModelRadio];

  /**
   * list of formfield types that get autofocus if they are the first field.
   */
  focusableFormFields = [FormModelText, FormModelNumber];

  /**
   * Textkey for the step headline
   */
  headlineTextkey: string = null;

  /**
   * textkey for the help on the right side
   */
  helpTextkey: string = null;

  /**
   * The stepform element
   */
  @ViewChild('stepForm') stepForm: ElementRef;

  /**
   * indicator if the current step has help or not.
   */
  hasHelp: boolean = true;

  /**
   * toggle hor showing/hiding the help (on small screens)
   */
  showPageHelp: boolean = false;

  /**
   * indicator if the current step has inline help or not.
   */
  hasInlineHelp: boolean = false;

  /**
   * toggle for showing/hiding the inline help
   */
  showInlineHelp: boolean = false;

  /**
   * remeber all subscriptions of this component to unsubscribe them ondestroy
   */
  subscriptions: Subscription[] = [];

  /**
   * if set to a string value, a form error is shown
   */
  formError: string = null;

  /**
   * if set to a string, a form warning is shown
   */
  formWarning: string = null;

  /**
   * flag to avoid race conditions if a steps is already moved away
   */
  moved: boolean = false;

  /**
   * toggle to de/activate the autoscroll when loading the step.
   */
  autoscrollOnLoad: boolean = true;

  /**
   * toggle for showing/hiding a loading animation on the next button.
   */
  loading: boolean = false;

  /**
   * vertical offset for the help text while scrolling
   */
  helpTextOffset: number = 0;

  /**
   * Startpoint of the help text
   */
  helpTextInitialOffset: number = 0;

  /**
   * scrolloffset if embeded
   */
  scrollOffset: number = 0;

  /**
   * The help text wrapper
   */
  helpWrapper: HTMLElement;

  /**
   * help text element that should be fixed while scrolling
   */
  stepHelp: HTMLElement;

  /**
   * holds some errors placeholder
   */
  stepErrorPlaceholder: any;

  /**
   * toggle for auto submit: activated after mouse inputs, deactivated after keyboard inputs
   */
  isMouseInteraction: boolean;

  /**
   * constructor
   * @param stepService
   * @param router
   * @param elementService
   */
  constructor(protected stepService: StepService, protected router: Router, protected elementService: ElementService) {}

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // get the current pre/next steps and subscribe on changes of these steps.
    this.steps = this.stepService.getSteps();
    this.onStepUpdate();

    this.subscriptions.push(this.stepService.stepsChanged.subscribe(steps => {
      this.steps = steps;
      this.onStepUpdate();
    }));

    // event listener for user interactions via mouse clicks or keyboard interactions
    window.addEventListener('mousedown', () => this.isMouseInteraction = true);
    window.addEventListener('keydown', () => this.isMouseInteraction = false);

    if (this.formConfig && this.steps.currentStep) {
      // create the form of the current step
      this.form = this.formConfig.getForm(this.steps.currentStep.name);

      this.setAutoSubmitAndInlineHelp();

      // subscribe to the status change of the form
      this.subscriptions.push(this.form.statusChanges.subscribe(() => {
        // execute the autosubmit only if the view is rendered, otherwise the user cannot go back.
        if (this.viewRendered) {
          // this is an autosubmit step and the form is valid => submit with a small delay
          setTimeout(() => {
            // from the last changes, the active fields could have been changed, update the autocommit status
            this.setAutoSubmitAndInlineHelp();
            if (this.autoSubmit && this.form.valid === true && this.moved === false) {
              this.onSubmit();
            }
          }, this.autoSubmitDelay);
        }
      }));
    }

    // get the scrolloffset if we are embeded
    if (this.elementService.embedService.isEmbedded()) {
      this.subscriptions.push(this.elementService.embedService.scrollEvent.subscribe(data => this.scrollOffset = data.offset));
    }
  }

  /**
   * Sets the autocommit flag and inline help flag, based on the active fields
   */
  setAutoSubmitAndInlineHelp(): void {
    // should the form be submitted automatically?
    const activeGroupFields = this.steps.currentStep ? this.formConfig.getFieldsFromGroup(this.steps.currentStep.name).filter(field => field.isActive() === true && field.type != 'hidden') : [];
    // activate autosubmit for mouse clicks
    if (this.isMouseInteraction) {
      // check if we have fields in the step that are NOT of any type in this.autoSubmitFieldTypes
      this.autoSubmit = activeGroupFields.filter(field => this.autoSubmitFieldTypes.filter(type => field instanceof type).length === 0).length === 0;
    }
    // deactivate autosubmit for keyboard navigation
    else {
      this.autoSubmit = false;
    }

    // check if we need to show inline help text
    this.hasInlineHelp = activeGroupFields.length > 0 && activeGroupFields.findIndex(field => field.hasInfotext()) > -1;
  }

  /**
   * @inheritDoc
   */
  ngOnDestroy(): void {
    // unsubscribe all subscriptions
    this.subscriptions.forEach(subscription => subscription.unsubscribe());

    // remove resize and scroll event for help text
    window.removeEventListener('resize', this.windowResize.bind(this));
    window.removeEventListener('scroll', this.windowScroll.bind(this));
  }

  /**
   * Steps are changed, some config/data has to be changed
   */
  onStepUpdate(): void {
    // check what is the previous step
    this.previousStep = this.getNextStepFromList(this.steps.previousStepList);

    if (this.steps.currentStep) {
      // build the headline textkey
      const stepTextkey = this.steps.currentStep.name.indexOf('/') > 0 ? this.steps.currentStep.name.substring(0, this.steps.currentStep.name.indexOf('/')) : this.steps.currentStep.name;
      this.headlineTextkey = this.textkeyPrefix + '.stepname.' + stepTextkey;

      // build the helptextkey
      this.helpTextkey = this.textkeyPrefix + '.help.' + this.steps.currentStep.name.replace('/', '.');
    }
  }

  /**
   * @inheritDoc
   */
  ngAfterViewInit(): void {
    // get all formfields
    const groupFields = this.steps?.currentStep && this.formConfig ? this.formConfig.getFieldsFromGroup(this.steps.currentStep.name) : [];

    // if the first field is an input, set the focus
    if (this.autoSubmit === false && groupFields.length > 0 && this.focusableFormFields.findIndex(type => groupFields[0] instanceof type) > -1) {
      // focus the first input, this should also lead to scrolling upwards. Make sure this is done AFTER the step animation is finished. Otherwise the step animation sucks
      window.setTimeout(() => document.getElementById(groupFields[0].getIds()[0])?.focus(), 400);
    }
    else if (this.autoscrollOnLoad === true && this.stepForm?.nativeElement) {
      // scroll upwards to the main headline
      window.setTimeout(() => {
        this.elementService.scrollToElement(this.stepForm.nativeElement);
      }, 200)
    }

    this.viewRendered = true;

    setTimeout(() => {
      // scroll to the top after init
      this.helpWrapper = document.querySelector('.help-wrapper');
      this.stepHelp = document.querySelector('.step-help');

      // check if there is a help text and init resize and scroll event
      if (this.helpWrapper && this.stepHelp) {
        window.addEventListener('resize', this.windowResize.bind(this))
        window.addEventListener('scroll', this.windowScroll.bind(this));
        this.initHelpTextOffset();

        // if advisor is embedded
        if (this.elementService.embedService.isEmbedded()) {
          this.subscriptions.push(
            this.elementService.embedService.scrollEvent.subscribe(data => this.updateHelpTextPosition(data))
          );
        }
      }
    }, 500);

    // TODO: check why there is the fllowing line, leads to scrolling to top after load of every advisor.
    // this.elementService.scrollToElement(document.querySelector('.' + StepNavigationComponent.STEPNAVIGATION_CLASS) || this.helpWrapper);

  }

  /**
   * in order to be able to add/remove resize event, we put method initHelpTextOffset in the windowResize
   */
  windowResize() {
    this.initHelpTextOffset();
  }

  /**
   * in order to be able to add/remove scroll event, we put method updateHelpTextPosition in the windowScroll
   */
  windowScroll() {
    this.updateHelpTextPosition();
  }

  /**
   * Save the data. Can be overwritten by child classes to add some logic there.
   * @param data
   */
  saveData(data: { [key: string]: any }): void {}

  /**
   * If the currect step is submitted, this method is called.
   */
  onSubmit(): void {
    this.formError = null;

    if (this.form.valid) {
      // save the data of all inputs
      this.saveData(this.formConfig.getData());

      this.moveToNextStep();
    }
    else {
      if (this.form.errors) {
        Object.keys(this.form.errors).map(key => this.formError = key);
      }
      else {
        // some field is missing
        this.formError = 'emptyfield';
      }
    }
  }

  /**
   * function will be called if the nextButton is clicked. Can be overwritten by children classes
   */
  submitClicked(): void {}

  /**
   * moveon to the next step in the routing
   */
  moveToNextStep(): void {
    // get the next step that either has no fields or at least one active field
    const nextStep = this.getNextStepFromList(this.steps.nextStepList);

    if (!nextStep) {
      console.error('step missing', nextStep, this.steps.nextStepList);
      return;
    }

    this.moved = true;
    // move on to the next step
    this.router.navigateByUrl(nextStep.path);
  }

  /**
   * Returns the next step that should be shown, based on the current formconfig.
   * Either the step has no field at all, or at least one active field.
   */
  getNextStepFromList(stepList: Step[], formConfig: FormConfig = null): any {
    if (!stepList || stepList.length === 0) {
      return null;
    }

    if (!this.formConfig) {
      return stepList[0];
    }

    return stepList.find(step => {
      const nextStepFields = formConfig ? formConfig.getFieldsFromGroup(step.name) : this.formConfig.getFieldsFromGroup(step.name);
      return nextStepFields.length === 0 || nextStepFields.filter(field => field.isActive()).length > 0;
    });
  }

  /**
   * toggles the visibility of the page help (only for small screens)
   */
  togglePageHelp(): void {
    this.showPageHelp = !this.showPageHelp;

    // show/hide also inline help if pagehelp is hidden
    this.showInlineHelp = this.showPageHelp;
  }

  /**
   * toggle the inline help
   */
  toggleInlineHelp(): void {
    this.showInlineHelp = !this.showInlineHelp;
  }

  /**
   * hide step help
   */
  hideHelp() {
    this.hasHelp = false;
  }


  /**
   * initialize the offset of the sticky help text
   */
  initHelpTextOffset(): void {
    if (this.stepHelp) {
      this.helpTextInitialOffset = ElementService.getTopOffset(this.stepHelp, 0) - this.helpTextOffset;

      if (this.elementService.embedService.isEmbedded()) {
        this.elementService.embedService.triggerFakeScroll();

        const stickyHeader = PortalService.getConfig('embed.stickyheader.mobile');
        if (stickyHeader) {
          this.helpTextInitialOffset -= parseFloat(stickyHeader);
        }
      }
    }
  }

  /**
   * Update the vertical help text position
   * @param data
   */
  updateHelpTextPosition(data: { position: number, offset: number } = null): void {
    // get current scrolling position
    const scrollPosition: number = data ? data.offset * -1 : window.scrollY;
    // calculate maximum scrolling position
    const elementHeight: number = this.stepHelp.offsetHeight;
    const wrapperHeight: number = this.helpWrapper.offsetHeight;
    const maxScroll = wrapperHeight - elementHeight;

    if (elementHeight > window.innerHeight) {
      // stop here, we have a very small window
      return;
    }

    // set the needed offset
    this.helpTextOffset = Math.max(0, Math.min(maxScroll, scrollPosition - this.helpTextInitialOffset));
    this.stepHelp.style.top = this.helpTextOffset + 'px';
  }

}
